import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useTheme} from "../../../../context/themeContext";
import {removeEntity} from "../../../../redux/entity/entity-reducer";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName, selectSelectedFeatures} from "../../../../redux/selectors/selectors";
import EntityForm from "../../../../components/Entities/EntityForm/EntityForm";
import {LoadingComponent} from "../../../../components/LoadingComponent/LoadingComponent";
import {useEntityLoader} from "../../../../hooks/entites/useEntityLoader";
import {isErrorResponse} from "../../../../utils/utils";
import {useToastContext} from "../../../../context/toastContext";
import './group-selection.scss';
import ErrorComponent from "../../../../components/ErrorComponent/ErrorComponent";
import {ApiError} from "../../../../api/RestClient";
import {usePresetsContext} from "../../../../context/presetsContext";
import {setSourceReloadRequired} from "../../../../redux/map/map-reducer";
import {clearedSelectedMapFeatures} from "../../../../redux/showInfo/showInfoReducer";
import {useFeaturesHighlightingContext} from "../../../../context/featuresHighlightingContext";
import {FormDataObj} from "../../../../utils/types";
import {RoadInfrastructureEntityName} from "../../../../api/types/types";


const GroupSelection = () => {
    const entityName = 'replancity_RoadInfrastructureUpdateSegment';
    const segmentsEntityName = RoadInfrastructureEntityName.RoadSegment;
    const segmentsSourceId = useTypedSelector((state) => selectMapLayerIdByEntityName(state, segmentsEntityName));
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const [loading, setLoading] = useState<boolean>(false);
    const {
        loading: isSegmentLoading,
        entity,
        metadata,
        error,
        loadData,
        changeForm,
        groupSaveFn,
        groupDeleteFn
    } = useEntityLoader<any>({entityName});
    const {selectedPresetId} = usePresetsContext();
    const {highlightedFeaturesById} = useFeaturesHighlightingContext();

    const {theme} = useTheme();
    const {addToast} = useToastContext();
    const {t} = useTranslation();
    const dispatch = useDispatch();


    useEffect(() => {
        void (async function () {
            await loadData({entityName});
        }());

        return () => {
            dispatch(removeEntity(entityName));
        }
    }, []);

    const submitFn = useCallback(async (formDataObj: FormDataObj) => {
        setLoading(true);

        const segmentIds = selectedFeatures.map(({id}) => id);
        const resp = await groupSaveFn({
                entityName,
                ids: segmentIds,
                data: formDataObj,
            }
        );

        if (!isErrorResponse(resp)) {
            addToast(`${t('common.save-successful')}`, 'success');
            await loadData({entityName});
            dispatch(setSourceReloadRequired({sourceId: segmentsSourceId, isReloadRequired: true}));
        }

        setLoading(false);

        return resp;
    }, [selectedFeatures, entityName])

    const deleteSelectedFn = useCallback(async () => {
        setLoading(true);

        const resp = await groupDeleteFn({presetId: selectedPresetId, ids: selectedFeatures.map(({id}) => id)});
        if (!isErrorResponse(resp)) {
            dispatch(setSourceReloadRequired({sourceId: segmentsSourceId, isReloadRequired: true}));
            dispatch(clearedSelectedMapFeatures());
            highlightedFeaturesById.current = {};
            addToast(`${t('common.delete-successful')}`, 'success');
        }

        setLoading(false);
    }, [groupDeleteFn, segmentsSourceId, selectedPresetId, selectedFeatures])

    return (
        <div className="group-selection">
            <p data-testid="groupSelectionSelectedNum">{t('road-network.group-selection-title', {numItems: selectedFeatures.length})}</p>
            <p className="description">{t('road-network.group-selection-description')}</p>
            <ErrorComponent error={error as unknown as ApiError}>
                <LoadingComponent isLoading={isSegmentLoading || loading}>
                    {
                        selectedFeatures.length
                            ? <EntityForm
                                loading={isSegmentLoading || loading}
                                entity={entity}
                                metadata={metadata}
                                changeFormFn={changeForm}
                                saveFn={submitFn}
                                showDeleteBtn={!!selectedFeatures.length}
                                deleteTooltip={selectedFeatures.length ? t('road-network.group-deletion-label') : ''}
                                deleteFn={deleteSelectedFn}
                                deleteModalTitle={t('road-network.group-deletion-label')}
                            />
                            : null
                    }
                </LoadingComponent>
            </ErrorComponent>
        </div>
    )
}

export default GroupSelection;